<script setup lang="ts">
  import { computed } from 'vue';
  import { SelectionSection, CustomCheckbox } from '@/shared/ui';

  export interface SelectableItem {
    id: number;
    name: string;
  }

  const props = defineProps<{
    mode: 'positions' | 'functionalActivities';
    selectedIds: string[];
    items: SelectableItem[];
    isLoading?: boolean;
    hasError?: boolean;
    title?: string;
    heightClass?: string;
  }>();

  const emit = defineEmits<{
    (e: 'update:selectedIds', value: string[]): void;
  }>();

  const resolvedTitle = computed(
    () => props.title ?? (props.mode === 'positions' ? 'Cargos' : 'Atividades Funcionais')
  );

  const resolvedColumnLabel = computed(() =>
    props.mode === 'positions' ? 'Cargos' : 'Atividades funcionais'
  );

  const selectAllLabel = computed(() =>
    props.mode === 'positions'
      ? 'Selecionar todos os cargos'
      : 'Selecionar todas as atividades funcionais'
  );

  const countLabel = computed(() =>
    props.mode === 'positions'
      ? 'Total de cargos selecionados:'
      : 'Total de atividades funcionais selecionadas:'
  );

  const resolveEmptyMessage = (): string => {
    if (props.hasError) {
      return 'Erro ao carregar itens.';
    }
    if (props.mode === 'positions') {
      return 'Nenhum cargo encontrado.';
    }
    return 'Nenhuma atividade funcional encontrada.';
  };

  const emptyMessage = computed(() => resolveEmptyMessage());

  const selectionCount = computed(() => props.selectedIds.length);

  const updateSelectedIds = (value: string[]) => {
    emit('update:selectedIds', value);
  };

  const toggleSelection = (event: Event, itemId: string) => {
    const { checked } = event.target as HTMLInputElement;
    updateSelectedIds(
      checked
        ? [...props.selectedIds, itemId]
        : props.selectedIds.filter((selectedId) => selectedId !== itemId)
    );
  };

</script>

<template>
  <div class="w-full min-w-0">
    <SelectionSection
      :title="resolvedTitle"
      :select-all-label="selectAllLabel"
      :items="props.isLoading || props.items.length === 0 ? [] : props.items"
      :selected-items="selectedIds"
      :count="selectionCount"
      :count-label="countLabel"
      :height-class="props.heightClass"
      @update:selected-items="updateSelectedIds"
    >
      <template #columns>
        <th class="pb-4 text-left font-semibold text-gray-07">
          {{ resolvedColumnLabel }}
        </th>
      </template>

      <template #item="{ item }">
        <td class="pb-3">
          <label class="flex cursor-pointer items-center gap-3 text-gray-07">
            <CustomCheckbox
              :id="`${props.mode}-${item.id}`"
              :value="item.id.toString()"
              :checked="selectedIds.includes(item.id.toString())"
              @change="toggleSelection($event, item.id.toString())"
            />
            <span>{{ item.name }}</span>
          </label>
        </td>
      </template>
    </SelectionSection>

    <div
      v-if="props.isLoading"
      data-testid="position-selector-loading"
      class="-mt-30 px-6 py-8 text-center text-sm text-gray-05"
    >
      Carregando...
    </div>

    <div
      v-else-if="props.items.length === 0"
      data-testid="position-selector-empty"
      class="-mt-30 px-6 py-8 text-center text-sm text-gray-05"
    >
      {{ emptyMessage }}
    </div>
  </div>
</template>
